import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { listCategories } from '../../infra/categoriesRepoRead';
import type { CategoryFilterState } from '../../domain/catalogCategoryTypes';
import type { CategoryRecord } from '../../domain/categoryTypes';
import { filterVisibleCategoryGroups, type VisibleCategoryGroup } from '../../domain/categoryMasterTree';
import { subscribeCatalogSynced } from '../../../sync/core/catalogSyncListeners';

type UseCategoryMasterListResult = {
  visibleGroups: VisibleCategoryGroup[];
  allCategories: CategoryRecord[];
  loading: boolean;
  error: string | null;
  itemCount: number;
  reload: (options?: { silent?: boolean }) => void;
  patchCategoryActive: (patch: Record<string, boolean>) => void;
  revertCategoryActivePatch: (ids: string[]) => void;
  /** Orden local pendiente de guardar (modo ordenar). Pisa sortOrder en la lista. */
  patchSortOrders: (items: { id: string; sortOrder: number }[]) => void;
  clearSortOrderPatch: () => void;
};

export function useCategoryMasterList(
  filterState: CategoryFilterState,
  searchQuery: string,
): UseCategoryMasterListResult {
  const [allCategories, setAllCategories] = useState<CategoryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [activeOverrides, setActiveOverrides] = useState<Record<string, boolean>>({});
  const [sortOverrides, setSortOverrides] = useState<Record<string, number>>({});
  const silentReloadRef = useRef(false);

  const reload = useCallback((options?: { silent?: boolean }) => {
    silentReloadRef.current = options?.silent ?? false;
    setReloadToken((token) => token + 1);
  }, []);

  const patchCategoryActive = useCallback((patch: Record<string, boolean>) => {
    setActiveOverrides((prev) => ({ ...prev, ...patch }));
  }, []);

  const revertCategoryActivePatch = useCallback((ids: string[]) => {
    setActiveOverrides((prev) => {
      if (ids.length === 0) return {};
      const next = { ...prev };
      for (const id of ids) delete next[id];
      return next;
    });
  }, []);

  const patchSortOrders = useCallback((items: { id: string; sortOrder: number }[]) => {
    setSortOverrides((prev) => {
      const next = { ...prev };
      for (const item of items) next[item.id] = item.sortOrder;
      return next;
    });
  }, []);

  const clearSortOrderPatch = useCallback(() => {
    setSortOverrides({});
  }, []);

  useEffect(() => {
    let alive = true;

    const run = async () => {
      if (!silentReloadRef.current) {
        setLoading(true);
      }
      setError(null);
      try {
        const rows = await listCategories({ includeInactive: true });
        if (!alive) return;
        setAllCategories(rows);
        setActiveOverrides((prev) => {
          const next: Record<string, boolean> = {};
          for (const [id, value] of Object.entries(prev)) {
            const row = rows.find((category) => category.id === id);
            if (row && row.isActive !== value) next[id] = value;
          }
          return next;
        });
        setSortOverrides((prev) => {
          const next: Record<string, number> = {};
          for (const [id, value] of Object.entries(prev)) {
            const row = rows.find((category) => category.id === id);
            if (row && row.sortOrder !== value) next[id] = value;
          }
          return next;
        });
      } catch (fetchError) {
        if (!alive) return;
        const message =
          fetchError instanceof Error ? fetchError.message : 'No se pudo cargar la lista.';
        setAllCategories([]);
        setActiveOverrides({});
        setError(message);
      } finally {
        silentReloadRef.current = false;
        if (alive) setLoading(false);
      }
    };

    void run();

    return () => {
      alive = false;
    };
  }, [reloadToken]);

  // Snapshot refresh (realtime masters / sync manual) → releer el cache.
  useEffect(() => subscribeCatalogSynced(() => reload({ silent: true })), [reload]);

  const mergedCategories = useMemo(() => {
    const hasActive = Object.keys(activeOverrides).length > 0;
    const hasSort = Object.keys(sortOverrides).length > 0;
    if (!hasActive && !hasSort) return allCategories;
    return allCategories.map((category) => {
      const isActive = activeOverrides[category.id];
      const sortOrder = sortOverrides[category.id];
      if (isActive === undefined && sortOrder === undefined) return category;
      return {
        ...category,
        isActive: isActive ?? category.isActive,
        sortOrder: sortOrder ?? category.sortOrder,
      };
    });
  }, [activeOverrides, sortOverrides, allCategories]);

  const visibleGroups = useMemo(
    () => filterVisibleCategoryGroups(mergedCategories, filterState, searchQuery),
    [mergedCategories, filterState, searchQuery],
  );

  return {
    visibleGroups,
    allCategories: mergedCategories,
    loading,
    error,
    itemCount: visibleGroups.length,
    reload,
    patchCategoryActive,
    revertCategoryActivePatch,
    patchSortOrders,
    clearSortOrderPatch,
  };
};
